import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

/**
 * Extra breathing room (px) kept between the scrolled-to position and the
 * viewport edge so content does not sit flush against it.
 */
export const SCROLL_MARGIN = 32;

/**
 * Calculates the cumulative offset from the editor DOM to its scroll container.
 * Walks up the DOM tree accumulating offsetTop values.
 *
 * @param editorDom - The editor's DOM element
 * @param scrollContainer - The scroll container element
 * @returns The total offset in pixels
 */
export function calculateOffsetTopFromScrollContainer(
	editorDom: Element,
	scrollContainer: HTMLElement,
): number {
	let offset = 0;
	let el: Element | null = editorDom;

	while (el && el !== scrollContainer) {
		if (el instanceof HTMLElement) {
			offset += el.offsetTop;
			el = el.offsetParent;
		} else {
			break;
		}
	}

	return offset;
}

/**
 * Content-space offset of `pos` from the top of `scrollParent`, using the same
 * `coordsAtPos` math as the scroll helpers (so tests can drive it without layout).
 */
function getPosOffsetInScrollContainer({
	view,
	pos,
	scrollParent,
}: {
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): number {
	const startCoords = view.coordsAtPos(pos);
	const offsetTop = view.coordsAtPos(0).top;
	const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
		view.dom,
		scrollParent,
	);
	return offsetTopFromScrollContainer + startCoords.top - offsetTop;
}

/**
 * True when the top of `pos` already sits inside the scroll parent's viewport.
 * Chat View changes uses this so an already-visible first change does not jump.
 */
function isPosInViewport({
	view,
	pos,
	scrollParent,
}: {
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): boolean {
	const posTop = getPosOffsetInScrollContainer({ view, pos, scrollParent });
	const viewportTop = scrollParent.scrollTop;
	const viewportBottom = viewportTop + scrollParent.offsetHeight;
	return posTop >= viewportTop && posTop < viewportBottom;
}

/**
 * Calculates the scroll position needed to show `pos` at the top of the viewport.
 *
 * @returns The target scrollTop value
 */
export function calculateTopScroll({
	view,
	pos,
	scrollParent,
}: {
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): number {
	return getPosOffsetInScrollContainer({ view, pos, scrollParent }) - SCROLL_MARGIN;
}

/**
 * Calculates the scroll position needed to show `pos` at the bottom of the viewport.
 * Accounts for modal height so a modal anchored below `pos` is not hidden.
 *
 * @returns The target scrollTop value
 */
export function calculateBottomScroll({
	view,
	pos,
	scrollParent,
	modalHeight,
}: {
	modalHeight: number;
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): number {
	const offsetTop = view.coordsAtPos(0).top;
	const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
		view.dom,
		scrollParent,
	);

	// Handle edge case where coords could be 0 at the last position of doc
	let targetBottom = view.coordsAtPos(pos).bottom;
	if (targetBottom === 0 && pos > 0) {
		targetBottom = view.coordsAtPos(pos - 1).bottom;
	}

	return (
		offsetTopFromScrollContainer +
		targetBottom -
		offsetTop +
		modalHeight +
		SCROLL_MARGIN -
		scrollParent.offsetHeight
	);
}

/**
 * Scrolls `pos` into view within the editor's scroll container, but ONLY when it
 * is not already visible:
 *   - below the viewport → scroll down so `pos` sits near the bottom, reserving
 *     `modalHeight` for the modal anchored beneath it;
 *   - above the viewport → scroll up so the range's START (`startPos`) sits near
 *     the top;
 *   - already visible     → no-op.
 *
 * Targeting the END when scrolling down and the START when scrolling up means a
 * change taller than the viewport still lands with the relevant edge (and the
 * modal, on the way down) in view, instead of cutting the segment off.
 *
 * Used by the Post Stream Review screen to keep the modal's anchor (the current
 * change) in view without the jarring always-scroll behaviour of the diff
 * overlay's own `scrollIntoView`, which force-scrolls to the FIRST change every
 * time regardless of what is already on screen.
 *
 * Defaults to `behavior: 'instant'`. Pass `'smooth'` for a visible scroll
 * (chat View changes). Modal re-anchors stay instant so the toolbar does not
 * lag behind the document.
 *
 * Pass `align: 'start'` to put the range START at the top of the viewport
 * when it is off-screen (chat View changes). If the start is already visible
 * this is a no-op. Default `'nearest'` keeps the modal helper's above/below
 * behaviour; `start` is used instead of `nearest` so an off-screen first
 * change is not pinned to the bottom, which would cut off later diffs.
 */
export function scrollRangeIntoView({
	view,
	startPos,
	endPos,
	modalHeight,
	behavior = 'instant',
	align = 'nearest',
}: {
	align?: 'nearest' | 'start';
	behavior?: ScrollBehavior;
	endPos: number;
	modalHeight: number;
	startPos: number;
	view: EditorView;
}): void {
	const scrollParent = findOverflowScrollParent(view.dom);
	if (!scrollParent) {
		return;
	}
	// Wrapped in try/catch because positions can be transiently invalid during
	// document mutations / remaps.
	try {
		const size = view.state.doc.content.size;
		const clampedStart = Math.min(Math.max(startPos, 0), size);
		const clampedEnd = Math.min(Math.max(endPos, 0), size);

		if (align === 'start') {
			// Already on screen → leave the viewport alone. When we do scroll,
			// pin START to the top so later highlighted changes stay visible.
			if (isPosInViewport({ view, pos: clampedStart, scrollParent })) {
				return;
			}
			const topScrollTop = Math.max(
				0,
				calculateTopScroll({ view, pos: clampedStart, scrollParent }),
			);
			scrollParent.scroll({ top: topScrollTop, behavior });
			return;
		}

		// Below the viewport → scroll down so the END sits near the bottom, leaving
		// room for the modal anchored beneath it.
		const bottomScrollTop = calculateBottomScroll({
			view,
			pos: clampedEnd,
			scrollParent,
			modalHeight,
		});
		if (scrollParent.scrollTop < bottomScrollTop) {
			scrollParent.scroll({ top: bottomScrollTop, behavior });
			return;
		}

		// Above the viewport → scroll up so the START sits near the top.
		const topScrollTop = calculateTopScroll({ view, pos: clampedStart, scrollParent });
		if (scrollParent.scrollTop > topScrollTop) {
			scrollParent.scroll({ top: topScrollTop, behavior });
		}
	} catch {
		// Silently ignore — positions may be invalid during rapid document mutations.
	}
}
